import React, { useState, useEffect } from 'react';
import { Grid, GridColumn, GridEditChangeEvent, GridItemChangeEvent } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { useCpsDialog } from '../../../common/useCpsDialog';
import {
  useGlobalizationContext,
  useTranslation,
  useNotificationContext,
  useGetFieldTranslations,
  useGetAllFields,
  useGetUserPermissions,
  useCreateUpdateFieldTranslations
} from '@cs-cms/state-management';
import { CpsIndex, CpsField, FieldTranslationsResponse, CreateUpdateFieldTranslationsRequest } from '@cs-cms/types';

interface GridDataItem {
  id: number;
  fieldId: string;
  internalName: string;
  displayName?: string;
  reference: string;
  translation: string;
}

interface DynamicTranslationsGridProps {
  activeLocale?: { id: number; name: string };
  englishLocale?: { id: number; name: string };
  selectedIndex?: CpsIndex | null;
}

export const DynamicTranslationsGrid: React.FC<DynamicTranslationsGridProps> = ({
  activeLocale,
  englishLocale,
  selectedIndex
}) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showDialog, DialogComponent } = useCpsDialog();
  const { showToast } = useNotificationContext();
  const [hasChanges, setHasChanges] = useState(false);
  const [gridData, setGridData] = useState<GridDataItem[]>([]);
  const [editState, setEditState] = useState<EditDescriptor>({});
  const {
    data: fieldTranslationsData,
    isLoading,
    error
  } = useGetFieldTranslations(selectedIndex?.id, activeLocale?.id);

  const { data: englishTranslations } = useGetFieldTranslations(selectedIndex?.id, englishLocale?.id);
  const { data: allFields } = useGetAllFields(undefined, selectedIndex?.id);
  const { data: permissions } = useGetUserPermissions();
  const createUpdateMutation = useCreateUpdateFieldTranslations();

  const fieldsMap = new Map<string, CpsField>();
  if (allFields) {
    allFields.forEach(field => {
      fieldsMap.set(field.id.toString(), field);
    });
  }

  let availableFieldIds: string[] = [];
  if (allFields) {
    const nonTechnicalFieldIds = allFields.filter(field => !field.technical).map(field => field.id.toString());
    const nonTechnicalFieldIdSet = new Set(nonTechnicalFieldIds);
    const translationFieldIds = fieldTranslationsData ? Object.keys(fieldTranslationsData) : [];
    const combinedIds = new Set([...translationFieldIds, ...nonTechnicalFieldIds]);

    availableFieldIds = Array.from(combinedIds).filter(fieldId => nonTechnicalFieldIdSet.has(fieldId));
  }

  useEffect(() => {
    if (!allFields) {
      return;
    }

    const fieldsMapForEffect = new Map<string, CpsField>();
    allFields.forEach(field => {
      fieldsMapForEffect.set(field.id.toString(), field);
    });

    const nonTechnicalFieldIds = allFields.filter(field => !field.technical).map(field => field.id.toString());
    const nonTechnicalFieldIdSet = new Set(nonTechnicalFieldIds);
    const translationFieldIds = fieldTranslationsData ? Object.keys(fieldTranslationsData) : [];
    const combinedIds = new Set([...translationFieldIds, ...nonTechnicalFieldIds]);
    const fieldIdsForEffect = Array.from(combinedIds).filter(fieldId => nonTechnicalFieldIdSet.has(fieldId));

    const newGridData = fieldIdsForEffect.map(fieldId => {
      const field = fieldsMapForEffect.get(fieldId);
      const englishReference = englishTranslations?.[fieldId] || field?.name || `Field ${fieldId}`;
      const currentTranslation = fieldTranslationsData?.[fieldId];

      return {
        id: parseInt(fieldId),
        fieldId,
        internalName: field?.name || `Field ${fieldId}`,
        displayName: field?.displayName,
        reference: englishReference,
        translation: currentTranslation || englishReference
      };
    });

    const newEditState: EditDescriptor = {};
    newGridData.forEach(item => {
      newEditState[item.id] = ['translation'];
    });

    setGridData(newGridData);
    setEditState(newEditState);
    setHasChanges(false);
  }, [allFields, fieldTranslationsData, englishTranslations]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    const field = event.field;
    if (!field) {
      return;
    }

    const newData = gridData.map(item => (item.id === event.dataItem.id ? { ...item, [field]: event.value } : item));
    setGridData(newData);
    setHasChanges(true);
  };

  const handleEditChange = (event: GridEditChangeEvent) => {
    setEditState(event.edit);
  };

  const handleSave = async () => {
    if (selectedIndex?.id === undefined || activeLocale?.id === undefined) {
      return;
    }

    const translations: FieldTranslationsResponse = {};
    gridData.forEach(item => {
      translations[item.fieldId] = item.translation;
    });

    const request: CreateUpdateFieldTranslationsRequest = {
      indexId: selectedIndex.id,
      localeId: activeLocale.id,
      translations
    };

    try {
      await createUpdateMutation.mutateAsync(request);
      setHasChanges(false);
      showToast({
        message: translate('custom.settings.adminCenter.translationManagement.saveSuccess'),
        type: 'success'
      });
    } catch (err: any) {
      showToast({
        message: err.message || translate('custom.settings.adminCenter.translationManagement.saveError'),
        type: 'error'
      });
    }
  };

  const handleSaveClick = () => {
    showDialog({
      title: translate('custom.common.save'),
      message: translate('custom.settings.adminCenter.translationManagement.confirmationMessage'),
      onConfirm: async confirmed => {
        if (confirmed) {
          await handleSave();
        }
      },
      confirmButtonLabel: translate('custom.common.save'),
      discardButtonLabel: translate('custom.common.cancel')
    });
  };

  const handleCancel = () => {
    if (fieldTranslationsData) {
      const resetData = availableFieldIds.map(fieldId => {
        const field = fieldsMap.get(fieldId);
        const englishReference = englishTranslations?.[fieldId] || field?.name || `Field ${fieldId}`;
        const currentTranslation = fieldTranslationsData[fieldId];

        return {
          id: parseInt(fieldId),
          fieldId,
          internalName: field?.name || `Field ${fieldId}`,
          displayName: field?.displayName,
          reference: englishReference,
          translation: currentTranslation || englishReference
        };
      });
      setGridData(resetData);
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <div className="translationsLoading">
        {translate('custom.settings.adminCenter.translationManagement.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="translationsError">
        {translate('custom.settings.adminCenter.translationManagement.errorLoading')}
      </div>
    );
  }

  const canEditDynamicTranslations = permissions?.editFieldTranslations;

  return (
    <>
      <div className="translationsTableContainer">
        <Grid
          data={gridData}
          sortable={true}
          className="translationsGrid"
          dataItemKey="id"
          edit={editState}
          editable={{ mode: 'incell' }}
          onItemChange={handleItemChange}
          onEditChange={handleEditChange}
        >
          <GridColumn
            field="internalName"
            title={translate('custom.settings.adminCenter.translationManagement.internalName')}
            width="250px"
            editable={false}
          />
          <GridColumn
            field="reference"
            title={translate('custom.settings.adminCenter.translationManagement.reference')}
            width="200px"
            editable={false}
          />
          <GridColumn
            field="translation"
            title={`${activeLocale?.name || translate('custom.settings.adminCenter.translationManagement.translation')}`}
            editor="text"
          />
        </Grid>
      </div>

      <div className="translationsFooter">
        <div className="translationsInfo">
          {`${gridData.length} `}
          {translate('custom.settings.adminCenter.translationManagement.fields')}
        </div>
        <div className="translationsActions">
          <Button onClick={handleCancel} disabled={!hasChanges} fillMode="outline" className="translationsCancelBtn">
            {translate('custom.common.cancel')}
          </Button>
          <Button
            onClick={handleSaveClick}
            disabled={!canEditDynamicTranslations || !hasChanges || createUpdateMutation.isPending}
            themeColor="primary"
            className="translationsSaveBtn"
          >
            {createUpdateMutation.isPending ? translate('custom.common.saving') : translate('custom.common.save')}
          </Button>
        </div>
      </div>

      {DialogComponent}
    </>
  );
};
